import { api, apiUrl } from '../utils/api.js';

// Admin Panel Component
export function adminPanel() {
  return {
    activeSection: 'overview',
    loading: false,
    
    // Modal system
    modal: {
      isOpen: false,
      type: null,
      title: '',
      data: null,
      formData: {}
    },
    
    // Maintenance
    maintenance: {
      enabled: false,
      message: ''
    },
    
    // System Metrics
    systemMetrics: {
      uptime: 0,
      summary: {
        totalRequests: 0,
        totalRoutes: 0
      },
      topRoutes: [],
      recentErrors: [],
      chartData: {
        requests: [],
        errors: [],
        response_times: []
      }
    },
    
    // Players
    playersSearch: {
      email: ''
    },
    playersList: {
      data: [],
      total: 0,
      page: 1,
      pageSize: 20
    },
    
    // Economy
    economyOverride: {
      enabled: false,
      market: 1.0,
      travel: 1.0
    },
    economyLogs: {
      data: [],
      total: 0,
      page: 1,
      pageSize: 20
    },
    
    // Content Management
    contentType: 'cities',
    cities: [],
    items: [],
    missions: [],
    recipes: [],
    
    // Feature Flags
    featureFlags: {
      data: [],
      total: 0,
      page: 1,
      pageSize: 10
    },
    
    // Ops
    canRunCron: !!import.meta.env.VITE_CRON_TOKEN,
    cronRunning: false,
    healthStatus: null,
    
    // Import/Export
    importExport: {
      activeType: 'cities',
      importing: false,
      exporting: false,
      importFile: null,
      exportData: null,
      importResults: null
    },
    
    // Audit Logs
    auditLogs: {
      data: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: {
        action: '',
        adminEmail: '',
        dateFrom: '',
        dateTo: ''
      }
    },
    
    async init() {
      console.log('Admin panel initializing...');
      
      // Only load if user has ADMIN role
      if (!this.$store.app?.auth?.user?.role || this.$store.app.auth.user.role !== 'ADMIN') {
        console.log('⚠️ Admin: User is not admin, skipping initialization');
        return;
      }
      
      // Don't load immediately - wait for tab to be activated
      this.$watch('$store.app.ui.activeTab', (tab) => {
        if (tab === 'admin' && !this._dataLoaded) {
          console.log('🔒 Admin tab activated, loading data...');
          this._dataLoaded = true;
          this.loadOverviewData();
        }
      });
      
      // If admin tab is already active, load immediately
      if (this.$store.app.ui.activeTab === 'admin') {
        this._dataLoaded = true;
        await this.loadOverviewData();
      }
    },
    
    // Overview Section
    async loadOverviewData() {
      try {
        this.loading = true;
        
        // Load maintenance config
        const maintenanceResponse = await api.admin.maintenanceGet();
        if (maintenanceResponse.success) {
          this.maintenance = maintenanceResponse.data;
        }
        
        // Load system metrics
        const metricsResponse = await api.admin.metrics();
        if (metricsResponse.success) {
          this.systemMetrics = metricsResponse.data;
          
          // Initialize charts after data is loaded
          this.$nextTick(() => {
            this.initializeCharts();
          });
        }
        
        // Load health status
        await this.checkHealth();
        
      } catch (error) {
        console.error('Error loading overview data:', error);
        this.$store.app.addNotification('Error loading admin overview', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async updateMaintenance() {
      try {
        const response = await api.admin.maintenanceSet({
          enabled: this.maintenance.enabled,
          message: this.maintenance.message
        });
        
        if (response.success) {
          this.maintenance = response.data;
          this.showToast('Maintenance config updated', 'success');
        }
      } catch (error) {
        console.error('Error updating maintenance:', error);
        this.showToast('Error updating maintenance config', 'error');
      }
    },
    
    async checkHealth() {
      try {
        const response = await api.admin.health();
        if (response.success) {
          this.healthStatus = response.data;
        }
      } catch (error) {
        console.error('Error checking health:', error);
        this.showToast('Error checking system health', 'error');
      }
    },
    
    // Players Section
    async loadPlayers() {
      try {
        this.loading = true;
        const query = new URLSearchParams({
          page: this.playersList.page,
          pageSize: this.playersList.pageSize,
          ...(this.playersSearch.email && { email: this.playersSearch.email })
        }).toString();
        
        const response = await api.admin.players.list(`?${query}`);
        if (response.success) {
          this.playersList.data = response.data;
          this.playersList.total = response.total;
        }
      } catch (error) {
        console.error('Error loading players:', error);
        this.showToast('Error loading players', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    changePage(delta) {
      this.playersList.page = Math.max(1, this.playersList.page + delta);
      this.loadPlayers();
    },
    
    async viewPlayer(playerId) {
      try {
        const response = await api.admin.players.get(playerId);
        if (response.success) {
          // For now, just show in console - could open a modal
          console.log('Player details:', response.data);
          this.showToast('Player details logged to console', 'info');
        }
      } catch (error) {
        console.error('Error viewing player:', error);
        this.showToast('Error loading player details', 'error');
      }
    },
    
    // This will be replaced by the new modal system
    showGrantModalOld(player) {
      // Simple implementation - in a real app you'd want a proper modal
      const coins = prompt(`Grant coins to ${player.user.email}:`);
      const xp = prompt('Grant XP:');
      
      if (coins || xp) {
        this.grantToPlayer(player.id, { 
          ...(coins && { coins: parseInt(coins) }),
          ...(xp && { xp: parseInt(xp) })
        });
      }
    },
    
    async grantToPlayer(playerId, grant) {
      try {
        const response = await api.admin.players.grant(playerId, grant);
        if (response.success) {
          this.showToast('Items granted successfully', 'success');
          await this.loadPlayers(); // Refresh list
        }
      } catch (error) {
        console.error('Error granting to player:', error);
        this.showToast('Error granting items to player', 'error');
      }
    },
    
    // Economy Section
    async loadEconomyData() {
      if (this.activeSection !== 'economy') return;
      
      try {
        this.loading = true;
        
        // Load economy factors
        const factorsResponse = await api.admin.economy.factors();
        if (factorsResponse.success) {
          // Load override from flags
          const flagsResponse = await api.admin.flagsList('?pageSize=50');
          if (flagsResponse.success) {
            const overrideFlag = flagsResponse.data.find(f => f.key === 'economy.overrideFactors');
            if (overrideFlag) {
              this.economyOverride = overrideFlag.value;
            }
          }
        }
        
        // Load economy logs
        const query = new URLSearchParams({
          page: this.economyLogs.page,
          pageSize: this.economyLogs.pageSize
        }).toString();
        
        const logsResponse = await api.admin.economy.logs(`?${query}`);
        if (logsResponse.success) {
          this.economyLogs.data = logsResponse.data;
          this.economyLogs.total = logsResponse.total;
        }
        
      } catch (error) {
        console.error('Error loading economy data:', error);
        this.showToast('Error loading economy data', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async updateEconomyOverride() {
      try {
        const response = await api.admin.economy.override(this.economyOverride);
        if (response.success) {
          this.showToast('Economy override updated', 'success');
        }
      } catch (error) {
        console.error('Error updating economy override:', error);
        this.showToast('Error updating economy override', 'error');
      }
    },
    
    // Content Section
    async loadContentData() {
      if (this.activeSection !== 'content') return;
      
      try {
        this.loading = true;
        
        // Load all content types
        const [citiesResponse, itemsResponse, missionsResponse, recipesResponse] = await Promise.all([
          api.admin.cities.list(),
          api.admin.items.list(),
          api.admin.missions.list(),
          api.admin.crafting.list()
        ]);
        
        if (citiesResponse.success) this.cities = citiesResponse.data;
        if (itemsResponse.success) this.items = itemsResponse.data;
        if (missionsResponse.success) this.missions = missionsResponse.data;
        if (recipesResponse.success) this.recipes = recipesResponse.recipes;
        
      } catch (error) {
        console.error('Error loading content data:', error);
        this.showToast('Error loading content data', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    showCreateModal(type) {
      // Use the new modal system
      switch (type) {
        case 'city':
          this.showCreateCityModal();
          break;
        case 'item':
          this.showCreateItemModal();
          break;
        case 'mission':
          this.showCreateMissionModal();
          break;
        case 'recipe':
          this.showToast('Create recipe modal not implemented yet', 'info');
          break;
      }
    },
    
    async createContent(type, data) {
      try {
        let response;
        if (type === 'city') response = await api.admin.cities.create(data);
        else if (type === 'item') response = await api.admin.items.create(data);
        else if (type === 'mission') response = await api.admin.missions.create(data);
        
        if (response.success) {
          this.showToast(`${type} created successfully`, 'success');
          await this.loadContentData(); // Refresh
        }
      } catch (error) {
        console.error(`Error creating ${type}:`, error);
        this.showToast(`Error creating ${type}`, 'error');
      }
    },
    
    editContent(type, item) {
      // Use the new modal system
      this.showEditModal(type, item);
    },
    
    async deleteContent(type, id) {
      if (!confirm(`Are you sure you want to delete this ${type}?`)) return;
      
      try {
        let response;
        if (type === 'city') response = await api.admin.cities.remove(id);
        else if (type === 'item') response = await api.admin.items.remove(id);
        else if (type === 'mission') response = await api.admin.missions.remove(id);
        else if (type === 'recipe') response = await api.admin.crafting.remove(id);
        
        if (response.success) {
          this.showToast(`${type} deleted successfully`, 'success');
          await this.loadContentData(); // Refresh
        }
      } catch (error) {
        console.error(`Error deleting ${type}:`, error);
        this.showToast(`Error deleting ${type}`, 'error');
      }
    },
    
    // Flags Section
    async loadFeatureFlags() {
      if (this.activeSection !== 'flags') return;
      
      try {
        this.loading = true;
        const query = new URLSearchParams({
          page: this.featureFlags.page,
          pageSize: this.featureFlags.pageSize
        }).toString();
        
        const response = await api.admin.flagsList(`?${query}`);
        if (response.success) {
          this.featureFlags.data = response.data;
          this.featureFlags.total = response.total;
        }
      } catch (error) {
        console.error('Error loading feature flags:', error);
        this.showToast('Error loading feature flags', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    editFlag(flag) {
      // Simple implementation - just prompt for new value
      const newValue = prompt(`Edit flag ${flag.key}:`, JSON.stringify(flag.value, null, 2));
      if (newValue !== null) {
        try {
          const parsed = JSON.parse(newValue);
          this.updateFlag(flag.key, parsed);
        } catch (error) {
          this.showToast('Invalid JSON format', 'error');
        }
      }
    },
    
    async updateFlag(key, value) {
      try {
        const response = await api.admin.flagUpsert(key, value);
        if (response.success) {
          this.showToast('Flag updated successfully', 'success');
          await this.loadFeatureFlags(); // Refresh
        }
      } catch (error) {
        console.error('Error updating flag:', error);
        this.showToast('Error updating flag', 'error');
      }
    },
    
    // Ops Section
    async runDailyCron() {
      if (!this.canRunCron) return;
      
      try {
        this.cronRunning = true;
        
        // Use the existing cron endpoint with token header
        const token = import.meta.env.VITE_CRON_TOKEN;
        const response = await fetch(apiUrl('/_cron/daily'), {
          method: 'POST',
          headers: {
            'X-Cron-Token': token,
            'Content-Type': 'application/json'
          }
        });
        
        const result = await response.json();
        
        if (result.success) {
          this.showToast('Daily cron completed successfully', 'success');
          console.log('Cron results:', result);
        } else {
          throw new Error(result.error || 'Cron failed');
        }
      } catch (error) {
        console.error('Error running daily cron:', error);
        this.showToast('Error running daily cron', 'error');
      } finally {
        this.cronRunning = false;
      }
    },
    
    // Import/Export Functions
    async loadImportExportData() {
      if (this.activeSection !== 'import-export') return;
      
      try {
        this.loading = true;
        
        // Load available content for export preview
        const [citiesResponse, itemsResponse, missionsResponse] = await Promise.all([
          api.admin.cities.list(),
          api.admin.items.list(),
          api.admin.missions.list()
        ]);
        
        if (citiesResponse.success) this.cities = citiesResponse.data;
        if (itemsResponse.success) this.items = itemsResponse.data;
        if (missionsResponse.success) this.missions = missionsResponse.data;
        
      } catch (error) {
        console.error('Error loading import/export data:', error);
        this.showToast('Error loading import/export data', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async exportContent() {
      try {
        this.importExport.exporting = true;
        
        const response = await api.admin.exportContent(this.importExport.activeType);
        if (response.success) {
          this.importExport.exportData = response.data;
          
          // Download the JSON file
          const blob = new Blob([JSON.stringify(response.data, null, 2)], {
            type: 'application/json'
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.importExport.activeType}-export-${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          this.showToast(`${this.importExport.activeType} exported successfully`, 'success');
        }
      } catch (error) {
        console.error('Error exporting content:', error);
        this.showToast('Error exporting content', 'error');
      } finally {
        this.importExport.exporting = false;
      }
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file && file.type === 'application/json') {
        this.importExport.importFile = file;
        this.showToast('File selected for import', 'info');
      } else {
        this.showToast('Please select a valid JSON file', 'error');
        event.target.value = '';
      }
    },
    
    async importContent() {
      if (!this.importExport.importFile) {
        this.showToast('Please select a file to import', 'error');
        return;
      }
      
      try {
        this.importExport.importing = true;
        this.importExport.importResults = null;
        
        // Read file content
        const fileContent = await this.readFileContent(this.importExport.importFile);
        let importData;
        
        try {
          importData = JSON.parse(fileContent);
        } catch (error) {
          throw new Error('Invalid JSON format');
        }
        
        // Validate import data structure
        if (!this.validateImportData(importData)) {
          throw new Error('Invalid data structure for import');
        }
        
        // Perform import
        const response = await api.admin.importContent(this.importExport.activeType, importData);
        
        if (response.success) {
          this.importExport.importResults = response.results;
          this.showToast(`Import completed: ${response.results.imported} items imported, ${response.results.skipped} skipped`, 'success');
          
          // Refresh content data
          await this.loadContentData();
        }
        
      } catch (error) {
        console.error('Error importing content:', error);
        this.showToast(`Import failed: ${error.message}`, 'error');
      } finally {
        this.importExport.importing = false;
      }
    },
    
    async readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => resolve(event.target.result);
        reader.onerror = error => reject(error);
        reader.readAsText(file);
      });
    },
    
    validateImportData(data) {
      const type = this.importExport.activeType;
      
      if (!Array.isArray(data)) {
        return false;
      }
      
      // Basic validation based on type
      switch (type) {
        case 'cities':
          return data.every(item => item.name && item.biome);
        case 'items':
          return data.every(item => item.name && item.type && typeof item.value === 'number');
        case 'missions':
          return data.every(item => item.name && item.difficulty && typeof item.rewardCoins === 'number');
        default:
          return false;
      }
    },
    
    clearImportData() {
      this.importExport.importFile = null;
      this.importExport.importResults = null;
      const fileInput = document.querySelector('#import-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    },
    
    getImportExportStats() {
      switch (this.importExport.activeType) {
        case 'cities':
          return { count: this.cities.length, type: 'Cities' };
        case 'items':
          return { count: this.items.length, type: 'Items' };
        case 'missions':
          return { count: this.missions.length, type: 'Missions' };
        default:
          return { count: 0, type: 'Unknown' };
      }
    },
    
    // Mini Charts Functions
    initializeCharts() {
      this.createRequestsChart();
      this.createErrorsChart();
      this.createResponseTimesChart();
    },
    
    createRequestsChart() {
      const canvas = document.getElementById('requests-chart');
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      
      // Generate sample data based on current metrics
      const data = this.generateTimeSeriesData(this.systemMetrics.summary.totalRequests, 24);
      
      new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: [{
            label: 'Requests',
            data: data.values,
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white'
            }
          },
          scales: {
            x: {
              display: false,
              grid: { display: false }
            },
            y: {
              display: false,
              grid: { display: false },
              beginAtZero: true
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          }
        }
      });
    },
    
    createErrorsChart() {
      const canvas = document.getElementById('errors-chart');
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      
      // Generate sample error data
      const data = this.generateTimeSeriesData(this.systemMetrics.recentErrors.length, 24, true);
      
      new Chart(ctx, {
        type: 'bar',
        data: {
          labels: data.labels,
          datasets: [{
            label: 'Errors',
            data: data.values,
            backgroundColor: 'rgba(239, 68, 68, 0.7)',
            borderColor: '#ef4444',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white'
            }
          },
          scales: {
            x: {
              display: false,
              grid: { display: false }
            },
            y: {
              display: false,
              grid: { display: false },
              beginAtZero: true
            }
          }
        }
      });
    },
    
    createResponseTimesChart() {
      const canvas = document.getElementById('response-times-chart');
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      
      // Generate sample response time data
      const avgResponseTime = this.systemMetrics.topRoutes.length > 0 
        ? this.systemMetrics.topRoutes.reduce((sum, route) => sum + (route.metrics?.avgMs || 0), 0) / this.systemMetrics.topRoutes.length
        : 100;
      
      const data = this.generateTimeSeriesData(avgResponseTime, 24, false, 0.3);
      
      new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: [{
            label: 'Response Time (ms)',
            data: data.values,
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              callbacks: {
                label: function(context) {
                  return `Response Time: ${context.parsed.y.toFixed(1)}ms`;
                }
              }
            }
          },
          scales: {
            x: {
              display: false,
              grid: { display: false }
            },
            y: {
              display: false,
              grid: { display: false },
              beginAtZero: true
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          }
        }
      });
    },
    
    generateTimeSeriesData(baseValue, points, allowZero = false, variance = 0.2) {
      const labels = [];
      const values = [];
      const now = new Date();
      
      for (let i = points - 1; i >= 0; i--) {
        const time = new Date(now.getTime() - (i * 60 * 60 * 1000)); // hourly data
        labels.push(time.getHours().toString().padStart(2, '0') + ':00');
        
        // Generate realistic variations
        let value = baseValue;
        if (allowZero && Math.random() < 0.3) {
          value = 0; // Some hours with no errors
        } else {
          const variation = (Math.random() - 0.5) * 2 * variance * baseValue;
          value = Math.max(allowZero ? 0 : 1, baseValue + variation);
        }
        
        values.push(Math.round(value));
      }
      
      return { labels, values };
    },
    
    refreshCharts() {
      // Destroy existing charts and recreate them
      Chart.helpers.each(Chart.instances, (instance) => {
        if (instance.canvas && document.body.contains(instance.canvas)) {
          instance.destroy();
        }
      });
      
      this.$nextTick(() => {
        this.initializeCharts();
      });
    },

    // Section switching with auto-loading
    async setActiveSection(section) {
      this.activeSection = section;
      
      // Auto-load data when switching sections
      switch (section) {
        case 'overview':
          await this.loadOverviewData();
          break;
        case 'players':
          await this.loadPlayers();
          break;
        case 'economy':
          await this.loadEconomyData();
          break;
        case 'content':
          await this.loadContentData();
          break;
        case 'flags':
          await this.loadFeatureFlags();
          break;
        case 'import-export':
          await this.loadImportExportData();
          break;
        case 'audit':
          await this.loadAuditLogs();
          break;
      }
    },
    
    // Audit Logs Functions
    async loadAuditLogs() {
      if (this.activeSection !== 'audit') return;
      
      try {
        this.loading = true;
        
        const query = new URLSearchParams({
          page: this.auditLogs.page,
          pageSize: this.auditLogs.pageSize,
          ...(this.auditLogs.filters.action && { action: this.auditLogs.filters.action }),
          ...(this.auditLogs.filters.adminEmail && { adminEmail: this.auditLogs.filters.adminEmail }),
          ...(this.auditLogs.filters.dateFrom && { dateFrom: this.auditLogs.filters.dateFrom }),
          ...(this.auditLogs.filters.dateTo && { dateTo: this.auditLogs.filters.dateTo })
        }).toString();
        
        const response = await api.admin.auditLogs(`?${query}`);
        if (response.success) {
          this.auditLogs.data = response.data;
          this.auditLogs.total = response.total;
        }
        
      } catch (error) {
        console.error('Error loading audit logs:', error);
        this.showToast('Error loading audit logs', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    changeAuditPage(delta) {
      this.auditLogs.page = Math.max(1, this.auditLogs.page + delta);
      this.loadAuditLogs();
    },
    
    filterAuditLogs() {
      this.auditLogs.page = 1; // Reset to first page
      this.loadAuditLogs();
    },
    
    clearAuditFilters() {
      this.auditLogs.filters = {
        action: '',
        adminEmail: '',
        dateFrom: '',
        dateTo: ''
      };
      this.auditLogs.page = 1;
      this.loadAuditLogs();
    },
    
    formatAuditDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    
    getActionBadgeClass(action) {
      const actionMap = {
        'CREATE': 'success',
        'UPDATE': 'warning',
        'DELETE': 'error',
        'EXPORT': 'info',
        'IMPORT': 'info',
        'GRANT': 'success',
        'CONFIG': 'warning'
      };
      return actionMap[action] || 'info';
    },
    
    getResourceIcon(resourceType) {
      const iconMap = {
        'City': '🏙️',
        'Item': '📦',
        'Mission': '🎯',
        'Player': '👤',
        'Config': '⚙️',
        'Economy': '💰',
        'Export': '📤',
        'Import': '📥'
      };
      return iconMap[resourceType] || '📄';
    },
    
    viewAuditDetails(log) {
      // Show audit log details in a modal or expanded view
      const details = {
        id: log.id,
        action: log.action,
        resource: log.resourceType,
        adminEmail: log.admin?.email || 'Unknown',
        timestamp: this.formatAuditDate(log.createdAt),
        details: log.details || {},
        metadata: log.metadata || {}
      };
      
      // For now, log to console - could open a modal
      console.log('Audit Log Details:', details);
      this.showToast('Audit details logged to console', 'info');
    },
    
    // Modal functions
    openModal(type, title, data = null) {
      this.modal = {
        isOpen: true,
        type,
        title,
        data,
        formData: this.getDefaultFormData(type, data)
      };
    },
    
    closeModal() {
      this.modal = {
        isOpen: false,
        type: null,
        title: '',
        data: null,
        formData: {}
      };
    },
    
    // Get default form data based on modal type
    getDefaultFormData(type, data = null) {
      switch (type) {
        case 'create-city':
          return { name: '', biome: '', description: '' };
        case 'create-item':
          return { name: '', type: '', value: 0, rarity: '', icon: '📦' };
        case 'create-mission':
          return { name: '', cityId: '', difficulty: '', rewardCoins: 0, duration: 300 };
        case 'grant-player':
          return { coins: 0, xp: 0 };
        case 'edit-city':
          return data ? { ...data } : {};
        case 'edit-item':
          return data ? { ...data } : {};
        case 'edit-mission':
          return data ? { ...data } : {};
        default:
          return {};
      }
    },
    
    // Handle modal form submission
    async handleModalSubmit() {
      try {
        this.loading = true;
        
        switch (this.modal.type) {
          case 'create-city':
            await this.handleCreateCity();
            break;
          case 'create-item':
            await this.handleCreateItem();
            break;
          case 'create-mission':
            await this.handleCreateMission();
            break;
          case 'grant-player':
            await this.handleGrantPlayer();
            break;
          case 'edit-city':
            await this.handleEditCity();
            break;
          case 'edit-item':
            await this.handleEditItem();
            break;
          case 'edit-mission':
            await this.handleEditMission();
            break;
        }
      } catch (error) {
        this.handleError(error, 'modal form submission');
      } finally {
        this.loading = false;
      }
    },
    
    // Modal form handlers
    async handleCreateCity() {
      const errors = this.validateForm(this.modal.formData, {
        name: { required: true },
        biome: { required: true }
      });
      
      if (errors) {
        this.showToast('Please fill in all required fields', 'error');
        return;
      }
      
      await this.createContent('city', this.modal.formData);
      this.closeModal();
    },
    
    async handleCreateItem() {
      const errors = this.validateForm(this.modal.formData, {
        name: { required: true },
        type: { required: true },
        value: { required: true, type: 'number', min: 0 },
        rarity: { required: true }
      });
      
      if (errors) {
        this.showToast('Please fill in all required fields correctly', 'error');
        return;
      }
      
      const itemData = {
        ...this.modal.formData,
        category: this.modal.formData.type,
        description: this.modal.formData.name,
        weight: 1.0,
        maxStack: 10,
        effects: {}
      };
      
      await this.createContent('item', itemData);
      this.closeModal();
    },
    
    async handleCreateMission() {
      const errors = this.validateForm(this.modal.formData, {
        name: { required: true },
        cityId: { required: true },
        difficulty: { required: true },
        rewardCoins: { required: true, type: 'number', min: 0 },
        duration: { required: true, type: 'number', min: 60 }
      });
      
      if (errors) {
        this.showToast('Please fill in all required fields correctly', 'error');
        return;
      }
      
      const missionData = {
        ...this.modal.formData,
        tier: this.modal.formData.difficulty,
        rewardXp: 25,
        baseMinutes: Math.ceil(this.modal.formData.duration / 60),
        risk: 10,
        requirements: {},
        rewards: { coins: this.modal.formData.rewardCoins }
      };
      
      await this.createContent('mission', missionData);
      this.closeModal();
    },
    
    async handleGrantPlayer() {
      const { coins, xp } = this.modal.formData;
      
      if (!coins && !xp) {
        this.showToast('Please enter coins or XP to grant', 'error');
        return;
      }
      
      const grant = {};
      if (coins) grant.coins = parseInt(coins);
      if (xp) grant.xp = parseInt(xp);
      
      await this.grantToPlayer(this.modal.data.id, grant);
      this.closeModal();
    },
    
    async handleEditCity() {
      // Implementation for editing cities
      this.showToast('Edit functionality coming soon', 'info');
      this.closeModal();
    },
    
    async handleEditItem() {
      // Implementation for editing items
      this.showToast('Edit functionality coming soon', 'info');
      this.closeModal();
    },
    
    async handleEditMission() {
      // Implementation for editing missions
      this.showToast('Edit functionality coming soon', 'info');
      this.closeModal();
    },
    
    // Enhanced create modals
    showCreateCityModal() {
      this.openModal('create-city', 'Create New City');
    },
    
    showCreateItemModal() {
      this.openModal('create-item', 'Create New Item');
    },
    
    showCreateMissionModal() {
      this.openModal('create-mission', 'Create New Mission');
    },
    
    // Enhanced grant modal
    showGrantModal(player) {
      this.openModal('grant-player', `Grant Items to ${player.user.email}`, player);
    },
    
    // Enhanced edit modals
    showEditModal(type, item) {
      this.openModal(`edit-${type}`, `Edit ${type}`, item);
    },
    
    // Form validation helpers
    validateForm(formData, rules) {
      const errors = {};
      
      for (const [field, rule] of Object.entries(rules)) {
        const value = formData[field];
        
        if (rule.required && (!value || value.toString().trim() === '')) {
          errors[field] = `${field} is required`;
        }
        
        if (rule.type === 'number' && value && isNaN(Number(value))) {
          errors[field] = `${field} must be a number`;
        }
        
        if (rule.min && Number(value) < rule.min) {
          errors[field] = `${field} must be at least ${rule.min}`;
        }
        
        if (rule.max && Number(value) > rule.max) {
          errors[field] = `${field} must be no more than ${rule.max}`;
        }
      }
      
      return Object.keys(errors).length > 0 ? errors : null;
    },
    
    // Enhanced toast notifications
    showToast(message, type = 'info', duration = 3000) {
      // Use app store notification system
      this.$store.app.addNotification(message, type);
    },
    
    // Enhanced error handling
    handleError(error, context = 'admin operation') {
      console.error(`Admin error in ${context}:`, error);
      
      let message = 'An unexpected error occurred';
      if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.message) {
        message = error.message;
      }
      
      this.showToast(`Error: ${message}`, 'error');
    },
    
    // Watch for section changes
    $watch: {
      activeSection(newSection) {
        this.setActiveSection(newSection);
      }
    }
  };
}